<template>
  <div class="activity-logs-panel">
    <v-row class="mb-2" dense>
      <v-col cols="12" sm="4">
        <v-select
            dense
            outlined
            hide-details
            clearable
            :items="dateRangeFilter"
            v-model="searchFormData.drSearch"
            item-text="title"
            item-value="value"
            label="Date range"
            @change="drChange"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
            type="date"
            dense
            outlined
            hide-details
            v-model="searchFormData.fromDateSearch"
            label="From date"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
            type="date"
            dense
            outlined
            hide-details
            v-model="searchFormData.toDateSearch"
            label="To date"
        ></v-text-field>
      </v-col>
      <v-col cols="12" class="d-flex align-center justify-space-between flex-wrap">
        <span v-if="!canSearch" class="caption grey--text">Select a record to load logs.</span>
        <v-spacer v-else></v-spacer>
        <v-btn color="#00004d" dark small :disabled="loading || !canSearch" @click="applyDateFilter">
          Apply
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-pagination
            color="#00004d"
            :total-visible="15"
            @input="refreshData(false)"
            :disabled="loading || !effectiveLogRecordId"
            v-model="pagination.page"
            :length="pageCount"
        ></v-pagination>
      </v-col>
    </v-row>
    <v-skeleton-loader :loading="loading" v-if="isLoaded" type="table"></v-skeleton-loader>
    <v-card-text v-if="!isLoaded" class="pa-0">
      <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalRecords"
          @update:items-per-page="refreshData(false)"
          :footer-props="{
            itemsPerPageOptions: rowsPerPageItems,
            showFirstLastPage: true
          }"
          :loading="loading"
          mobile-breakpoint="0"
          dense
          class="elevation-0 custom-logs-table"
      >
        <template v-slot:[`item.tbl_details`]="{ item }">
          <div class="d-flex align-center justify-center custom-logs-change-cell">
            <v-btn
                v-if="hasChangeDetailPayload(item)"
                icon
                x-small
                color="#00004d"
                title="View change detail"
                @click.stop="openChangeDetailModal(item)"
            >
              <v-icon x-small>fas fa-eye</v-icon>
            </v-btn>
            <span v-else class="grey--text">—</span>
          </div>
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDateAndTime(item.created_at) }}
        </template>
      </v-data-table>
    </v-card-text>
    <v-row>
      <v-col cols="12">
        <v-pagination
            color="#00004d"
            :total-visible="15"
            @input="refreshData(false)"
            :disabled="loading || !effectiveLogRecordId"
            v-model="pagination.page"
            :length="pageCount"
        ></v-pagination>
      </v-col>
    </v-row>

    <v-dialog v-model="changeDetailDialog" max-width="760" scrollable content-class="custom-logs-change-dialog">
      <v-card>
        <v-card-title class="headline pb-2" style="background: #00004d; color: #fff;">
          {{ changeDetailTitle }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <p v-if="!changeDetailSections.length" class="grey--text text--darken-1 mb-0">
            No structured change data for this entry.
          </p>
          <div v-for="(section, sIdx) in changeDetailSections" :key="'sec-' + sIdx" class="mb-4">
            <h4 class="subtitle-1 font-weight-bold mb-1">{{ section.title }}</h4>
            <p v-if="section.subtitle" class="caption grey--text text--darken-1 mb-2">{{ section.subtitle }}</p>
            <ul class="custom-logs-change-list pl-0 mb-0">
              <li v-for="(line, lIdx) in section.lines" :key="'ln-' + sIdx + '-' + lIdx" class="mb-2">
                <span class="font-weight-medium">{{ line.label }}:</span>
                <span class="red--text text--darken-2">{{ line.from }}</span>
                <span class="mx-1">→</span>
                <span class="green--text text--darken-2">{{ line.to }}</span>
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#00004d" dark text @click="changeDetailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {mapState} from "vuex";
import {GET_DEVELOPMENT_ACTIVITY_LOGS} from "@/core/services/store/developments.module";
import {GET_PROJECT_ACTIVITY_LOGS} from "@/core/services/store/projects.module";
import {GET_LISTING_ACTIVITY_LOGS} from "@/core/services/store/listings.module";
import {GET_LEAD_ACTIVITY_LOGS} from "@/core/services/store/leads.module";
import {GET_CONTACT_ACTIVITY_LOGS} from "@/core/services/store/contacts.module";
import customLogsGridMixin from "@/view/pages/realstate/logs/customLogsGrid.mixin";

export default {
  name: 'ActivityLogsPanel',
  mixins: [customLogsGridMixin],
  props: {
    developmentId: {
      type: Number,
      default: null,
    },
    /** When set, loads project-scoped activity logs (subject = Project). */
    projectId: {
      type: Number,
      default: null,
    },
    /** When set, loads listing-scoped activity logs (subject = Listing). */
    listingId: {
      type: Number,
      default: null,
    },
    /** When set, loads lead-scoped activity logs (subject = Lead). */
    leadId: {
      type: Number,
      default: null,
    },
    /** When set, loads contact-scoped activity logs (subject = Contact). */
    contactId: {
      type: Number,
      default: null,
    },
    /** Matches master (`0`) vs sub (`1`) developments listing scope (ignored when projectId is set). */
    developmentListType: {
      type: String,
      default: '0',
    },
  },
  data() {
    return {
      /** Slimmer grid than Custom logs: no Event, Page/API, or IP columns. */
      tableHeaders: [
        { text: 'Description', value: 'tbl_description', sortable: false, width: '26%' },
        { text: 'Record', value: 'tbl_record', sortable: false, width: '20%' },
        { text: 'User', value: 'tbl_done_by', sortable: false, width: '18%' },
        { text: 'Change details', value: 'tbl_details', sortable: false, width: '22%' },
        { text: 'Date', value: 'created_at', sortable: false, width: '14%' },
      ],
      draw: 1,
      loading: false,
      isLoaded: true,
      pagination: {
        page: 1,
        rowsPerPage: 25,
        itemsPerPage: 25,
        totalItems: 0,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      totalRecords: 0,
      headerindex: '',
      columns: [],
      headers: [
        {
          text: 'User',
          value: 'user_id',
          name: 'user_id',
          filterable: false,
          sortable: false
        },
        {
          text: "Created",
          value: "created_at",
          name: "created_at",
          filterable: false,
          sortable: true
        },
        {text: "Updated", value: "updated_at", name: "updated_at", filterable: false, sortable: false},
        {text: 'Action', value: 'actions', data: null, name: null, filterable: false, sortable: false},
      ],
      items: [],
      searchFormData: {
        drSearch: '1',
        fromDateSearch: '',
        toDateSearch: '',
      },
      dateRangeFilter: [
        {title: 'Last month', value: '1'},
        {title: 'Last 3 months', value: '2'},
        {title: 'Last 6 months', value: '3'},
        {title: 'Last year', value: '4'},
        {title: 'Year to date', value: '5'},
      ],
      searchQuery: '',
    };
  },
  mounted() {
    for (let i = 0; i < this.headers.length; i++) {
      this.columns.push(
          '&columns[' + i + '][data]=' + this.headers[i].data +
          '&columns[' + i + '][name]=' + this.headers[i].name +
          '&columns[' + i + '][searchable]=' + this.headers[i].filterable +
          '&columns[' + i + '][orderable]=' + this.headers[i].sortable +
          '&columns[' + i + '][search][value]=' +
          '&columns[' + i + '][search][regex]=' + false
      );
    }
    this.drChange('1');
    this.$nextTick(() => {
      if (this.effectiveLogRecordId) {
        this.refreshData(false);
      }
    });
  },
  watch: {
    developmentId() {
      this.onActivityLogRecordContextChange();
    },
    projectId() {
      this.onActivityLogRecordContextChange();
    },
    listingId() {
      this.onActivityLogRecordContextChange();
    },
    leadId() {
      this.onActivityLogRecordContextChange();
    },
    contactId() {
      this.onActivityLogRecordContextChange();
    },
  },
  methods: {
    onActivityLogRecordContextChange() {
      this.pagination.page = 1;
      const id = this.effectiveLogRecordId;
      if (id && this.searchFormData.fromDateSearch && this.searchFormData.toDateSearch) {
        this.refreshData(false);
      } else {
        this.items = [];
        this.totalRecords = 0;
      }
    },
    getHeaderIndex(name) {
      const that = this;
      this.headers.filter(function (ele, i) {
        if (name === ele.name) {
          that.headerindex = i;
        }
      });
    },
    loadData() {
      if (!this.effectiveLogRecordId) {
        this.loading = false;
        this.isLoaded = false;
        return Promise.resolve({items: [], total: 0});
      }
      this.loading = true;
      this.isLoaded = true;
      return new Promise((resolve) => {
        const params = this.params;
        const per = params.itemsPerPage || params.rowsPerPage || 25;
        params.length = per;
        params.start = params.page === 1 ? 0 : per * (params.page - 1);
        params.draw = this.draw;
        let query = Object.keys(params).map((key) => {
          return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
        }).join('&');
        const searchFormDataQuery = Object.keys(this.searchFormData).map((key) => {
          return encodeURIComponent(key) + '=' + encodeURIComponent(this.searchFormData[key] == null ? '' : this.searchFormData[key]);
        }).join('&');
        const sortBy = Array.isArray(params.sortBy) && params.sortBy.length ? params.sortBy : ['created_at'];
        const sortDesc = Array.isArray(params.sortDesc) ? params.sortDesc : [true];
        this.getHeaderIndex(sortBy[0]);
        query += '&' + searchFormDataQuery;
        query +=
            "&search[value]=" +
            params.query +
            "&search[regex]=" +
            false +
            "&order[0][column]=" +
            (sortBy.length === 0 ? 1 : this.headerindex) +
            "&order[0][dir]=" +
            (sortDesc[0] ? "desc" : "asc");
        const isListing = this.listingId != null;
        const isLead = !isListing && this.leadId != null;
        const isContact = !isListing && !isLead && this.contactId != null;
        const isProject = !isListing && !isLead && !isContact && this.projectId != null;
        if (!isProject && !isListing && !isLead && !isContact) {
          query += '&developmentListType=' + encodeURIComponent(this.developmentListType || '0');
        }
        const recordId = this.effectiveLogRecordId;
        const dispatchPayload = {q: query, columns: this.columns};
        const loadPromise = isListing
            ? this.$store.dispatch(GET_LISTING_ACTIVITY_LOGS, {...dispatchPayload, listingId: recordId})
            : isLead
            ? this.$store.dispatch(GET_LEAD_ACTIVITY_LOGS, {...dispatchPayload, leadId: recordId})
            : isContact
            ? this.$store.dispatch(GET_CONTACT_ACTIVITY_LOGS, {...dispatchPayload, contactId: recordId})
            : isProject
            ? this.$store.dispatch(GET_PROJECT_ACTIVITY_LOGS, {...dispatchPayload, projectId: recordId})
            : this.$store.dispatch(GET_DEVELOPMENT_ACTIVITY_LOGS, {...dispatchPayload, developmentId: recordId});
        loadPromise.then((res) => {
          this.draw++;
          const items = res.records.data;
          const total = res.records.recordsFiltered;
          this.loading = false;
          this.isLoaded = false;
          resolve({
            items,
            total
          });
        }).catch(() => {
          this.loading = false;
          this.isLoaded = false;
          resolve({items: [], total: 0});
        });
      });
    },
    refreshData() {
      this.loadData().then(response => {
        this.items = response.items;
        this.totalRecords = response.total;
      });
    },
    applyDateFilter() {
      this.pagination.page = 1;
      this.refreshData(false);
    },
    drChange(v) {
      if (v) {
        if (v === "1") {
          const date = new Date();
          const cdate = new Date();
          const lastmonths = new Date(cdate.setMonth(cdate.getMonth() - 1));
          const toDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          const fromDate = lastmonths.getFullYear() + '-' + ('0' + (lastmonths.getMonth() + 1)).slice(-2) + '-' + ('0' + lastmonths.getDate()).slice(-2);
          this.searchFormData.toDateSearch = toDate;
          this.searchFormData.fromDateSearch = fromDate;
        } else if (v === "2") {
          const date = new Date();
          const cdate = new Date();
          const last3months = new Date(cdate.setMonth(cdate.getMonth() - 2));
          const toDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          const fromDate = last3months.getFullYear() + '-' + ('0' + (last3months.getMonth() + 1)).slice(-2) + '-' + ('0' + last3months.getDate()).slice(-2);
          this.searchFormData.toDateSearch = toDate;
          this.searchFormData.fromDateSearch = fromDate;
        } else if (v === "3") {
          const date = new Date();
          const cdate = new Date();
          const last6months = new Date(cdate.setMonth(cdate.getMonth() - 5));
          const toDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          const fromDate = last6months.getFullYear() + '-' + ('0' + (last6months.getMonth() + 1)).slice(-2) + '-' + ('0' + last6months.getDate()).slice(-2);
          this.searchFormData.toDateSearch = toDate;
          this.searchFormData.fromDateSearch = fromDate;
        } else if (v === "4") {
          const date = new Date();
          const toDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          const fromDate = (date.getFullYear() - 1) + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          this.searchFormData.toDateSearch = toDate;
          this.searchFormData.fromDateSearch = fromDate;
        } else if (v === "5") {
          const date = new Date();
          const toDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
          const fromDate = date.getFullYear() + '-01-01';
          this.searchFormData.toDateSearch = toDate;
          this.searchFormData.fromDateSearch = fromDate;
        }
      } else {
        const cd = this.$store.state.auth.c_d;
        this.searchFormData.toDateSearch = cd;
        this.searchFormData.fromDateSearch = cd;
      }
    },
  },
  computed: {
    effectiveLogRecordId() {
      if (this.listingId != null) {
        return this.listingId;
      }
      if (this.leadId != null) {
        return this.leadId;
      }
      if (this.contactId != null) {
        return this.contactId;
      }
      if (this.projectId != null) {
        return this.projectId;
      }
      return this.developmentId;
    },
    params() {
      return {
        ...this.pagination,
        query: this.searchQuery
      };
    },
    canSearch() {
      return !!this.effectiveLogRecordId && !!this.searchFormData.fromDateSearch && !!this.searchFormData.toDateSearch;
    },
    pageCount() {
      const per = this.params.itemsPerPage || this.params.rowsPerPage || 25;
      return per <= this.totalRecords ? Math.ceil(this.totalRecords / per) : 0;
    },
    ...mapState({
      errors: state => state.developments.errors
    }),
  },
};
</script>

<style scoped>
.custom-logs-table ::v-deep td {
  vertical-align: top;
  white-space: normal !important;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.4;
}
.custom-logs-table ::v-deep th {
  white-space: nowrap;
}
.custom-logs-change-cell {
  min-height: 32px;
}
.custom-logs-change-list {
  list-style: none;
}
</style>
